import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { 
  ArrowLeft, 
  Users, 
  CreditCard, 
  TrendingUp, 
  Clock,
  UserPlus,
  Crown,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Star,
  MessageSquare,
  Check,
  X,
  Trash2,
  AlertTriangle,
  Search,
  Loader2,
  ShieldCheck,
  User,
  Mail,
  Send
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [recentSignups, setRecentSignups] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ pending_count: 0, approved_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [expandedUser, setExpandedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Payment verification state
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  // Stripe subscriptions state (orphan cleanup)
  const [stripeSubs, setStripeSubs] = useState(null);
  const [loadingStripeSubs, setLoadingStripeSubs] = useState(false);
  const [cancellingSubId, setCancellingSubId] = useState(null);

  // Change user email state
  const [oldEmail, setOldEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);
  const [emailChangeResult, setEmailChangeResult] = useState(null);

  // Win-back email state
  const [winbackCount, setWinbackCount] = useState(null);
  const [winbackLoading, setWinbackLoading] = useState(false);
  const [winbackSending, setWinbackSending] = useState(false);
  const [winbackResult, setWinbackResult] = useState(null);
  const [winbackConfirm, setWinbackConfirm] = useState(false);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token, filter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, usersRes, recentRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/admin/users?filter_type=${filter}&limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/admin/recent-signups`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/admin/reviews`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setRecentSignups(recentRes.data.recent_signups || []);
      setReviews(reviewsRes.data.reviews || []);
      setReviewStats({
        pending_count: reviewsRes.data.pending_count || 0,
        approved_count: reviewsRes.data.approved_count || 0
      });
    } catch (err) {
      console.error('Admin error:', err);
      if (err.response?.status === 403) {
        setError('Du har inte admin-behörighet');
      } else {
        setError('Kunde inte ladda data');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `${minutes} min sedan`;
    if (hours < 24) return `${hours} tim sedan`;
    if (days < 7) return `${days} dagar sedan`;
    return formatDate(dateStr);
  };

  const handleDeleteAllUsers = async () => {
    setActionLoading('delete-all');
    try {
      const res = await axios.delete(`${API}/api/admin/users/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      await loadData();
      alert(`${res.data.deleted_count} användare borttagna`);
    } catch (err) {
      alert('Kunde inte ta bort användare: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteTarget(null);
      await loadData();
    } catch (err) {
      alert('Kunde inte ta bort användare: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveReview = async (reviewId) => {
    setActionLoading(reviewId);
    try {
      await axios.put(`${API}/api/reviews/${reviewId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadData();
    } catch (err) {
      alert('Kunde inte godkänna recension: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    setActionLoading(`del-${reviewId}`);
    try {
      await axios.delete(`${API}/api/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadData();
    } catch (err) {
      alert('Kunde inte ta bort recension: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const filteredReviews = reviews.filter(r => {
    if (reviewFilter === 'pending') return !r.approved;
    if (reviewFilter === 'approved') return r.approved;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Crown className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold mb-2">Åtkomst nekad</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => navigate('/dashboard')} className="rounded-full">
            Tillbaka till Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const handleVerifyPayment = async (force = false) => {
    if (!verifyEmail.trim()) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await axios.post(`${API}/api/admin/verify-payment`, {
        email: verifyEmail.trim(),
        force,
        subscription_type: 'lifetime'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerifyResult(res.data);
      if (res.data.success) {
        toast.success(res.data.message);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Något gick fel';
      setVerifyResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  const loadStripeSubscriptions = async () => {
    setLoadingStripeSubs(true);
    try {
      const res = await axios.get(`${API}/api/admin/list-stripe-subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeSubs(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Kunde inte hämta Stripe-prenumerationer';
      toast.error(msg);
      setStripeSubs({ subscriptions: [], count: 0, error: msg });
    } finally {
      setLoadingStripeSubs(false);
    }
  };

  const handleCancelStripeSub = async (subscription_id, immediately = false) => {
    const label = immediately ? 'omedelbart' : 'vid periodslut';
    if (!window.confirm(`Säga upp prenumerationen ${subscription_id} ${label}?`)) return;
    setCancellingSubId(subscription_id);
    try {
      const res = await axios.post(`${API}/api/admin/cancel-stripe-subscription`, {
        subscription_id,
        immediately
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Prenumerationen avslutades');
      // Refresh list
      await loadStripeSubscriptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte säga upp prenumerationen');
    } finally {
      setCancellingSubId(null);
    }
  };

  const handleChangeUserEmail = async (e) => {
    e?.preventDefault?.();
    setEmailChangeResult(null);
    const oldE = oldEmail.trim().toLowerCase();
    const newE = newEmail.trim().toLowerCase();
    if (!oldE || !newE) {
      toast.error('Fyll i båda e-postadresserna');
      return;
    }
    if (oldE === newE) {
      toast.error('Ny e-postadress måste skilja sig från den gamla');
      return;
    }
    setChangingEmail(true);
    try {
      const res = await axios.post(`${API}/api/admin/change-user-email`, {
        old_email: oldE,
        new_email: newE,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailChangeResult({ success: true, ...res.data });
      toast.success(res.data.message);
      setOldEmail('');
      setNewEmail('');
      // Refresh user list so the change is visible
      try {
        const usersRes = await axios.get(`${API}/api/admin/users?filter_type=${filter}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(usersRes.data.users || []);
      } catch (e) { /* ignore */ }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Kunde inte ändra e-postadressen';
      setEmailChangeResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setChangingEmail(false);
    }
  };

  const loadWinbackCount = async () => {
    setWinbackLoading(true);
    setWinbackResult(null);
    try {
      const res = await axios.get(`${API}/api/admin/winback/eligible-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWinbackCount(res.data.eligible_count);
      setWinbackConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte hämta antalet');
    } finally {
      setWinbackLoading(false);
    }
  };

  const handleSendWinback = async () => {
    setWinbackSending(true);
    setWinbackResult(null);
    try {
      const res = await axios.post(`${API}/api/admin/winback/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWinbackResult({ success: true, ...res.data });
      setWinbackCount(0);
      setWinbackConfirm(false);
      toast.success(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Kunde inte skicka mejlen';
      setWinbackResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setWinbackSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-page">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="admin-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-lg font-semibold flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-500" />
                Admin Panel
              </h1>
              <p className="text-xs text-muted-foreground">Hantera användare, recensioner och se statistik</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={loadData} className="rounded-full" data-testid="admin-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" />
            Uppdatera
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-6 flex gap-1">
          {[
            { id: 'overview', label: 'Översikt', icon: TrendingUp },
            { id: 'users', label: 'Användare', icon: Users, count: stats?.total_users },
            { id: 'payments', label: 'Betalningar', icon: CreditCard },
            { id: 'reviews', label: 'Recensioner', icon: MessageSquare, count: reviewStats.pending_count > 0 ? reviewStats.pending_count : null }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">

        {/* ========== OVERVIEW TAB ========== */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl border p-4" data-testid="stat-total-users">
                <div className="flex items-center gap-2 text-blue-600 mb-2">
                  <Users className="w-5 h-5" />
                  <span className="text-sm font-medium">Totalt användare</span>
                </div>
                <p className="text-3xl font-bold">{stats?.total_users || 0}</p>
              </div>
              <div className="bg-white rounded-2xl border p-4" data-testid="stat-paying-users">
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <CreditCard className="w-5 h-5" />
                  <span className="text-sm font-medium">Betalande</span>
                </div>
                <p className="text-3xl font-bold">{stats?.paying_users || 0}</p>
              </div>
              <div className="bg-white rounded-2xl border p-4" data-testid="stat-trial-users">
                <div className="flex items-center gap-2 text-orange-600 mb-2">
                  <Clock className="w-5 h-5" />
                  <span className="text-sm font-medium">Provperiod</span>
                </div>
                <p className="text-3xl font-bold">{stats?.trial_users || 0}</p>
              </div>
              <div className="bg-white rounded-2xl border p-4" data-testid="stat-revenue">
                <div className="flex items-center gap-2 text-indigo-600 mb-2">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-sm font-medium">Totala intäkter</span>
                </div>
                <p className="text-3xl font-bold">{stats?.total_revenue_sek || 0} kr</p>
              </div>
            </div>

            {/* New signups */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 p-4">
                <p className="text-sm text-blue-700 font-medium">Nya idag</p>
                <p className="text-2xl font-bold text-blue-900">{stats?.users_today || 0}</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-100 p-4">
                <p className="text-sm text-green-700 font-medium">Denna vecka</p>
                <p className="text-2xl font-bold text-green-900">{stats?.users_this_week || 0}</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-100 p-4">
                <p className="text-sm text-purple-700 font-medium">Denna månad</p>
                <p className="text-2xl font-bold text-purple-900">{stats?.users_this_month || 0}</p>
              </div>
            </div>

            {/* Reviews summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border p-4" data-testid="stat-pending-reviews">
                <div className="flex items-center gap-2 text-amber-600 mb-2">
                  <Clock className="w-5 h-5" />
                  <span className="text-sm font-medium">Väntande recensioner</span>
                </div>
                <p className="text-3xl font-bold">{reviewStats.pending_count}</p>
              </div>
              <div className="bg-white rounded-2xl border p-4" data-testid="stat-approved-reviews">
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <Check className="w-5 h-5" />
                  <span className="text-sm font-medium">Godkända recensioner</span>
                </div>
                <p className="text-3xl font-bold">{reviewStats.approved_count}</p>
              </div>
            </div>

            {/* Recent Signups */}
            <div className="bg-white rounded-2xl border overflow-hidden">
              <div className="px-5 py-4 border-b bg-slate-50">
                <h2 className="font-semibold flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                  Senaste registreringar
                </h2>
              </div>
              <div className="divide-y">
                {recentSignups.length === 0 ? (
                  <p className="p-4 text-muted-foreground text-center">Inga registreringar än</p>
                ) : (
                  recentSignups.map((u, i) => (
                    <div key={i} className="px-5 py-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          u.is_subscribed 
                            ? 'bg-green-100 text-green-700' 
                            : u.is_trial 
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-gray-100 text-gray-700'
                        }`}>
                          {u.is_subscribed ? 'Betalande' : u.is_trial ? 'Provperiod' : 'Inaktiv'}
                        </span>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatRelativeDate(u.created_at)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* ========== USERS TAB ========== */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Delete all users button */}
            <div className="bg-white rounded-2xl border p-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Rensa alla användare
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Tar bort alla användare utom admin. Denna åtgärd kan inte ångras.
                </p>
              </div>
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={() => { setDeleteTarget('all'); setShowDeleteConfirm(true); }}
                className="rounded-full"
                data-testid="delete-all-users-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Rensa alla
              </Button>
            </div>

            {/* User list */}
            <div className="bg-white rounded-2xl border overflow-hidden">
              <div className="px-5 py-4 border-b bg-slate-50 flex items-center justify-between">
                <h2 className="font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Alla användare ({users.length})
                </h2>
                <div className="flex gap-2">
                  {['all', 'paying', 'trial'].map((f) => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      data-testid={`user-filter-${f}`}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        filter === f
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {f === 'all' ? 'Alla' : f === 'paying' ? 'Betalande' : 'Provperiod'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {users.length === 0 ? (
                  <p className="p-4 text-muted-foreground text-center">Inga användare hittades</p>
                ) : (
                  users.map((u, i) => (
                    <div key={i} className="px-5 py-3" data-testid={`user-row-${i}`}>
                      <button
                        onClick={() => setExpandedUser(expandedUser === i ? null : i)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <div>
                          <p className="font-medium">{u.name}</p>
                          <p className="text-sm text-muted-foreground">{u.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            u.is_subscribed 
                              ? 'bg-green-100 text-green-700' 
                              : u.is_trial 
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-gray-100 text-gray-700'
                          }`}>
                            {u.is_subscribed ? 'Betalande' : u.is_trial ? 'Provperiod' : 'Inaktiv'}
                          </span>
                          {expandedUser === i ? (
                            <ChevronUp className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                      </button>
                      {expandedUser === i && (
                        <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm space-y-2">
                          <p><strong>Registrerad:</strong> {formatDate(u.created_at)}</p>
                          {u.is_trial && (
                            <p><strong>Dagar kvar av provperiod:</strong> {u.trial_days_left}</p>
                          )}
                          {u.subscription_end && (
                            <p><strong>Prenumeration slutar:</strong> {formatDate(u.subscription_end)}</p>
                          )}
                          <div className="pt-2 border-t">
                            <Button 
                              variant="destructive" 
                              size="sm" 
                              onClick={() => { setDeleteTarget(u); setShowDeleteConfirm(true); }}
                              disabled={actionLoading === u.id}
                              className="rounded-full text-xs"
                              data-testid={`delete-user-${i}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Ta bort användare
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ========== PAYMENTS TAB ========== */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            {/* Verify Payment */}
            <div className="bg-white rounded-2xl border p-6 space-y-4" data-testid="verify-payment-section">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Verifiera betalning</h3>
                  <p className="text-sm text-muted-foreground">Kontrollera en kunds betalning mot Stripe och aktivera Premium</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Kundens e-postadress..."
                  value={verifyEmail}
                  onChange={(e) => { setVerifyEmail(e.target.value); setVerifyResult(null); }}
                  className="flex-1 rounded-xl"
                  data-testid="verify-email-input"
                />
                <Button
                  onClick={() => handleVerifyPayment(false)}
                  disabled={verifying || !verifyEmail.trim()}
                  className="rounded-full"
                  data-testid="verify-payment-btn"
                >
                  {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Verifiera
                </Button>
              </div>

              {verifyResult && (
                <div className={`rounded-xl p-4 ${verifyResult.success ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="verify-result">
                  <p className={`font-medium ${verifyResult.success ? 'text-green-700' : 'text-amber-700'}`}>
                    {verifyResult.success ? <Check className="w-4 h-4 inline mr-1" /> : <AlertTriangle className="w-4 h-4 inline mr-1" />}
                    {verifyResult.message}
                  </p>
                  {verifyResult.subscription_end && (
                    <p className="text-sm text-green-600 mt-1">
                      Premium aktiv till: {new Date(verifyResult.subscription_end).toLocaleDateString('sv-SE')}
                    </p>
                  )}
                  {!verifyResult.success && (
                    <div className="mt-3">
                      {verifyResult.pending_transactions > 0 && (
                        <p className="text-sm text-amber-600 mb-2">
                          {verifyResult.pending_transactions} väntande transaktioner hittades men ingen kunde verifieras via Stripe.
                        </p>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleVerifyPayment(true)}
                        disabled={verifying}
                        className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-100"
                        data-testid="force-premium-btn"
                      >
                        {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Crown className="w-4 h-4 mr-2" />}
                        Ge Premium manuellt (gratis)
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Change user email — for fixing customer typos */}
            <div className="bg-white rounded-2xl border p-6 space-y-4" data-testid="change-email-section">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Ändra användarens e-postadress</h3>
                  <p className="text-sm text-muted-foreground">Använd t.ex. när en kund skrivit fel e-post vid registrering</p>
                </div>
              </div>

              <form onSubmit={handleChangeUserEmail} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    type="email"
                    placeholder="Nuvarande (felaktig) e-post"
                    value={oldEmail}
                    onChange={(e) => setOldEmail(e.target.value)}
                    className="rounded-xl"
                    data-testid="change-email-old-input"
                  />
                  <Input
                    type="email"
                    placeholder="Ny (rätt) e-post"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="rounded-xl"
                    data-testid="change-email-new-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={changingEmail || !oldEmail.trim() || !newEmail.trim()}
                  className="rounded-full"
                  data-testid="change-email-btn"
                >
                  {changingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Ändra e-postadress
                </Button>
              </form>

              {emailChangeResult && (
                <div className={`rounded-xl p-3 text-sm ${emailChangeResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} data-testid="change-email-result">
                  {emailChangeResult.success ? <Check className="w-4 h-4 inline mr-1" /> : <AlertTriangle className="w-4 h-4 inline mr-1" />}
                  {emailChangeResult.message}
                  {emailChangeResult.success && (
                    <div className="text-xs text-green-600 mt-1">
                      {emailChangeResult.name && <span>Användare: <strong>{emailChangeResult.name}</strong> · </span>}
                      Premium: {emailChangeResult.is_premium ? '✅ aktiv' : '❌ inte aktiv'}
                      {!emailChangeResult.is_premium && (
                        <span className="block mt-1">
                          Tips: Använd "Verifiera betalning" ovan med den nya e-posten för att aktivera Premium om kunden redan betalat via Stripe.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Win-back email — re-engage expired-trial users */}
            <div className="bg-white rounded-2xl border p-6 space-y-4" data-testid="winback-section">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Win-back-mejl</h3>
                  <p className="text-sm text-muted-foreground">
                    Skicka ett mejl med rabattkoden LIVON (100 kr rabatt) till alla vars provperiod tagit slut och som inte betalat.
                    Varje användare får mejlet högst en gång.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadWinbackCount}
                  disabled={winbackLoading}
                  className="rounded-full"
                  data-testid="winback-count-btn"
                >
                  {winbackLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Räkna mottagare
                </Button>

                {winbackCount !== null && winbackCount > 0 && !winbackConfirm && (
                  <Button
                    size="sm"
                    onClick={() => setWinbackConfirm(true)}
                    className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="winback-prepare-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Förbered utskick
                  </Button>
                )}
              </div>

              {winbackCount !== null && !winbackResult && (
                <div className={`rounded-xl p-4 ${winbackCount > 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 border border-gray-200'}`} data-testid="winback-count-result">
                  {winbackCount > 0 ? (
                    <>
                      <p className="text-emerald-800 font-medium">
                        {winbackCount} användare är berättigade till win-back-mejlet
                      </p>
                      <p className="text-xs text-emerald-700 mt-1">
                        (provperiod utgången, inte premium, har inte fått win-back-mejlet tidigare)
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-700">Inga användare är berättigade just nu.</p>
                  )}
                </div>
              )}

              {winbackConfirm && (
                <div className="rounded-xl p-4 bg-amber-50 border border-amber-200 space-y-3" data-testid="winback-confirm">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-900">
                        Är du säker? Detta skickar {winbackCount} mejl via Resend.
                      </p>
                      <p className="text-amber-700 mt-1">
                        Det kan inte ångras och varje användare kan bara få mejlet en gång.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleSendWinback}
                      disabled={winbackSending}
                      className="rounded-full bg-amber-600 hover:bg-amber-700"
                      data-testid="winback-send-btn"
                    >
                      {winbackSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      Ja, skicka {winbackCount} mejl
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setWinbackConfirm(false)}
                      disabled={winbackSending}
                      className="rounded-full"
                      data-testid="winback-cancel-btn"
                    >
                      Avbryt
                    </Button>
                  </div>
                </div>
              )}

              {winbackResult && (
                <div className={`rounded-xl p-4 ${winbackResult.success ? 'bg-green-50 border border-green-200' : 'bg-rose-50 border border-rose-200'}`} data-testid="winback-result">
                  <p className={`font-medium ${winbackResult.success ? 'text-green-700' : 'text-rose-700'}`}>
                    {winbackResult.success ? <Check className="w-4 h-4 inline mr-1" /> : <AlertTriangle className="w-4 h-4 inline mr-1" />}
                    {winbackResult.message}
                  </p>
                </div>
              )}
            </div>

            {/* Stripe Subscriptions — orphan cleanup */}
            <div className="bg-white rounded-2xl border p-6 space-y-4" data-testid="stripe-subscriptions-section">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Aktiva Stripe-prenumerationer</h3>
                    <p className="text-sm text-muted-foreground">Hitta och säg upp gamla återkommande prenumerationer (t.ex. om en kund fortfarande debiteras månadsvis)</p>
                  </div>
                </div>
                <Button
                  onClick={loadStripeSubscriptions}
                  disabled={loadingStripeSubs}
                  variant="outline"
                  size="sm"
                  className="rounded-full shrink-0"
                  data-testid="load-stripe-subs-btn"
                >
                  {loadingStripeSubs ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  {stripeSubs ? 'Uppdatera' : 'Hämta lista'}
                </Button>
              </div>

              {stripeSubs && (
                <div className="space-y-3" data-testid="stripe-subs-list">
                  {stripeSubs.count === 0 ? (
                    <div className="text-center py-6 text-sm text-muted-foreground">
                      <Check className="w-6 h-6 text-green-500 mx-auto mb-2" />
                      Inga aktiva prenumerationer hittades. Allt är rent!
                    </div>
                  ) : (
                    stripeSubs.subscriptions.map((sub) => (
                      <div
                        key={sub.subscription_id}
                        className="border rounded-xl p-4 space-y-3"
                        data-testid={`stripe-sub-${sub.subscription_id}`}
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{sub.customer_email || '(okänd email)'}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                sub.status === 'active' ? 'bg-green-100 text-green-700' :
                                sub.status === 'trialing' ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {sub.status}
                              </span>
                              {!sub.in_livon_db && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-700" title="Stripe-kund saknar Livon-konto">
                                  Orphan
                                </span>
                              )}
                              {sub.cancel_at_period_end && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                  Uppsagd
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground font-mono">{sub.subscription_id}</p>
                            {sub.price?.amount && (
                              <p className="text-sm text-muted-foreground">
                                {sub.price.amount} {sub.price.currency?.toUpperCase()} / {sub.price.interval || 'engång'}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {!sub.cancel_at_period_end && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCancelStripeSub(sub.subscription_id, false)}
                                disabled={cancellingSubId === sub.subscription_id}
                                className="rounded-full"
                                data-testid={`cancel-period-end-${sub.subscription_id}`}
                              >
                                {cancellingSubId === sub.subscription_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Säg upp vid periodslut'}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCancelStripeSub(sub.subscription_id, true)}
                              disabled={cancellingSubId === sub.subscription_id}
                              className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
                              data-testid={`cancel-immediately-${sub.subscription_id}`}
                            >
                              Säg upp direkt
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== REVIEWS TAB ========== */}
        {activeTab === 'reviews' && (
          <div className="space-y-4">
            {/* Review stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-amber-50 rounded-2xl border border-amber-100 p-4" data-testid="reviews-pending-count">
                <p className="text-sm text-amber-700 font-medium">Väntande</p>
                <p className="text-2xl font-bold text-amber-900">{reviewStats.pending_count}</p>
              </div>
              <div className="bg-green-50 rounded-2xl border border-green-100 p-4" data-testid="reviews-approved-count">
                <p className="text-sm text-green-700 font-medium">Godkända</p>
                <p className="text-2xl font-bold text-green-900">{reviewStats.approved_count}</p>
              </div>
            </div>

            {/* Review filter */}
            <div className="flex gap-2">
              {['all', 'pending', 'approved'].map(f => (
                <button
                  key={f}
                  onClick={() => setReviewFilter(f)}
                  data-testid={`review-filter-${f}`}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    reviewFilter === f
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f === 'all' ? 'Alla' : f === 'pending' ? 'Väntande' : 'Godkända'}
                </button>
              ))}
            </div>

            {/* Review list */}
            <div className="space-y-3">
              {filteredReviews.length === 0 ? (
                <div className="bg-white rounded-2xl border p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Inga recensioner att visa</p>
                </div>
              ) : (
                filteredReviews.map((review) => (
                  <div 
                    key={review.id} 
                    className={`bg-white rounded-2xl border p-4 ${!review.approved ? 'border-amber-200' : ''}`}
                    data-testid={`review-card-${review.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{review.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            review.approved 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {review.approved ? 'Godkänd' : 'Väntande'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} 
                            />
                          ))}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatRelativeDate(review.created_at)}</span>
                    </div>
                    <p className="text-sm text-foreground mt-2 mb-3">{review.text}</p>
                    <div className="flex gap-2">
                      {!review.approved && (
                        <Button 
                          size="sm" 
                          onClick={() => handleApproveReview(review.id)}
                          disabled={actionLoading === review.id}
                          className="rounded-full text-xs"
                          data-testid={`approve-review-${review.id}`}
                        >
                          <Check className="w-3 h-3 mr-1" />
                          {actionLoading === review.id ? 'Godkänner...' : 'Godkänn'}
                        </Button>
                      )}
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={() => handleDeleteReview(review.id)}
                        disabled={actionLoading === `del-${review.id}`}
                        className="rounded-full text-xs"
                        data-testid={`delete-review-${review.id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        {actionLoading === `del-${review.id}` ? 'Tar bort...' : 'Ta bort'}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Bekräfta borttagning</h3>
                <p className="text-sm text-muted-foreground">
                  {deleteTarget === 'all' 
                    ? 'Vill du ta bort alla användare utom admin?' 
                    : `Vill du ta bort ${deleteTarget?.name || 'denna användare'}?`
                  }
                </p>
              </div>
            </div>
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg mb-4">
              Denna åtgärd kan inte ångras. All användardata kommer att raderas permanent.
            </p>
            <div className="flex gap-3 justify-end">
              <Button 
                variant="outline" 
                onClick={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}
                className="rounded-full"
                data-testid="cancel-delete-btn"
              >
                Avbryt
              </Button>
              <Button 
                variant="destructive"
                onClick={() => {
                  if (deleteTarget === 'all') {
                    handleDeleteAllUsers();
                  } else {
                    handleDeleteUser(deleteTarget.id);
                  }
                }}
                disabled={actionLoading}
                className="rounded-full"
                data-testid="confirm-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {actionLoading ? 'Tar bort...' : 'Ta bort'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
